// client/src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import API from './api';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

const isEdge = () => navigator.userAgent.includes('Edg/');
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

let currentUnsubscribe = null;

// ============================================================
// ✅ طلب الإذن وتسجيل التوكن
// ============================================================
export const requestNotificationPermission = async (userId, retryCount = 0) => {
  const MAX_RETRIES = 3;
  const edge = isEdge();

  if (Notification.permission === 'denied') {
    console.warn('⚠️ Notification permission denied');
    if (edge) {
      alert(
        '🔔 الإشعارات مرفوضة في متصفح Edge.\n' +
        'الرجاء السماح بها من إعدادات المتصفح:\n' +
        '1. افتح edge://settings/content/notifications\n' +
        '2. أضف هذا الموقع إلى القائمة المسموحة\n' +
        '3. أعد تحميل الصفحة'
      );
    }
    return null;
  }

  try {
    let registration;

    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    const existingSW = existingRegistrations.find(reg =>
      reg.active?.scriptURL.includes('firebase-messaging-sw')
    );

    if (existingSW) {
      //console.log('✅ Service Worker already registered, using existing');
      registration = existingSW;
    } else {
      if (edge) {
        for (const reg of existingRegistrations) {
          if (reg.active?.scriptURL.includes('firebase-messaging-sw')) {
            await reg.unregister();
            //console.log('🗑️ Edge: Unregistered old Firebase Service Worker');
          }
        }
        await delay(500);

        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });
       // console.log('✅ Edge: Service Worker registered');

        if (!registration.active) {
        //  console.log('⏳ Waiting for Service Worker to activate...');
          await new Promise((resolve) => {
            if (registration.active) {
              resolve();
            } else {
              registration.addEventListener('activate', resolve);
            }
          });
        //  console.log('✅ Service Worker activated');
        }
      } else {
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        await navigator.serviceWorker.ready;
      }
    }

    const sw = registration.active || (await navigator.serviceWorker.ready).active;
    if (!sw) {
      throw new Error('Service Worker not active');
    }
   // console.log('✅ Service Worker is active and ready');

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted');
        return null;
      }
    }

    const tokenOptions = {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    };

    const token = await getToken(messaging, tokenOptions);

    if (token) {
    //  console.log('✅ FCM Token obtained:', token);
      await saveTokenToServer(token, userId);
      return token;
    } else {
      console.warn('⚠️ No FCM token received');
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting FCM token:', error);

    if (error.name === 'AbortError' && retryCount < MAX_RETRIES) {
    //  console.log(`🔄 Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
      await delay(2000);
      if (edge) {
      //  console.log('🔧 Re-registering Service Worker for Edge...');
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            if (reg.active?.scriptURL.includes('firebase-messaging-sw')) {
              await reg.unregister();
          //    console.log('🗑️ Edge: Unregistered service worker before retry');
            }
          }
          await delay(500);
        } catch (e) {
          console.warn('⚠️ Could not unregister SW before retry:', e);
        }
      }
      return requestNotificationPermission(userId, retryCount + 1);
    }

    if (edge) {
      alert(
        '⚠️ تعذر تفعيل الإشعارات في Microsoft Edge.\n\n' +
        'يرجى التأكد من:\n' +
        '1. الموقع يعمل عبر HTTPS (وهو كذلك).\n' +
        '2. الإشعارات مسموحة في إعدادات Windows.\n' +
        '3. جرب استخدام متصفح Chrome كبديل.\n\n' +
        'إذا استمرت المشكلة، تواصل مع الدعم الفني.'
      );
    }

    return null;
  }
};

// ============================================================
// ✅ حفظ التوكن على الخادم
// ============================================================
const saveTokenToServer = async (token, userId) => {
  try {
    const response = await API.post('/notifications/fcm-token', {
      token,
      userId,
      deviceInfo: `${navigator.userAgent} (${navigator.platform})`,
    });
    if (response.status === 200 || response.status === 201) {
     // console.log('✅ FCM token saved to server');
    } else {
      throw new Error('Failed to save token');
    }
  } catch (error) {
    console.error('❌ Error saving token:', error);
    throw error;
  }
};

// ============================================================
// ✅ الاستماع للإشعارات في المقدمة (مع عرض إشعار النظام)
// ============================================================
export const listenForNotifications = (callback) => {
  // إلغاء المستمع القديم
  if (currentUnsubscribe) {
    currentUnsubscribe();
    currentUnsubscribe = null;
  //  console.log('🧹 Old FCM listener unsubscribed');
  }

  const unsubscribe = onMessage(messaging, (payload) => {
  //  console.log('📢 Foreground notification received:', payload);

    // استخراج البيانات
    const title = payload.notification?.title || payload.data?.title || 'Soccer Up';
    const body = payload.notification?.body || payload.data?.body || '';
    const data = payload.data || {};

    // ✅ 1. عرض إشعار النظام (Push Notification Banner)
    if (Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: body,
          icon: '/img/logo.png',
          data: data,
          requireInteraction: true,
          silent: false,
          tag: data.matchId || 'soccer-up-notification',
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          if (data.matchId) {
          //  console.log(`🔔 User clicked notification for match ${data.matchId}`);
          }
        };

        setTimeout(() => {
          notification.close();
        }, 10000);
      } catch (err) {
        console.warn('⚠️ Could not show system notification:', err);
      }
    }

    // ✅ 2. تمرير البيانات إلى الـ callback لعرض Toast داخل التطبيق
    if (callback) {
      callback({
        title,
        body,
        data,
      });
    }
  });

  currentUnsubscribe = unsubscribe;
  //console.log('✅ FCM listener registered (with system notification support)');

  return () => {
    if (unsubscribe) {
      unsubscribe();
      currentUnsubscribe = null;
     // console.log('🧹 FCM listener unsubscribed (manual)');
    }
  };
};

// ============================================================
// ✅ حذف توكن المستخدم (عند تسجيل الخروج) - استخدام POST بدلاً من DELETE
// ============================================================
export const deleteFCMToken = async (userId) => {
  try {
    // استخدام POST بدلاً من DELETE لتجنب مشاكل الـ Proxies والـ CDNs
    await API.post('/notifications/clear-fcm-tokens', {});
   // console.log('✅ FCM tokens cleared for user', userId);
  } catch (error) {
    // تجاهل أي خطأ، لأن العملية ليست حرجة
    console.warn('⚠️ FCM token clearing warning:', error.message);
  }
};

export default messaging;